import React from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import type { QuestionnaireCardData } from './types';
import { Tag } from '@/components/ui/tag';
import {
  getCardDescription,
  getCardEstimatedMinutes,
  getCardQuestionCount,
  getCardTitle,
} from './types';
import { getQuestionnaireAssignmentMeta } from './meta';
import {
  QUICK_ACTION_TOOLTIP_MESSAGE,
  QUICK_ACTION_TOOLTIP_SLOT_PROPS,
} from './quickActionTooltip';
import notebookIcon from '@/assets/images/notebook-new.svg';
import clockCircleIcon from '@/assets/images/clock-circle-new.svg';
import usersGroupRoundedIcon from '@/assets/images/users-group-rounded.svg';
import verifiedCheckIcon from '@/assets/images/verified-check.svg';

const cardHeadingSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: 'clamp(1.05rem, 2.4vw, 1.21rem)',
  lineHeight: '135%',
  letterSpacing: '-0.02em',
  color: '#0f172a',
  overflowWrap: 'anywhere',
} as const;

interface QuestionnaireCardProps {
  card: QuestionnaireCardData;
  onPreview: () => void;
  onResponses: () => void;
  onAssign: () => void;
  responsesLabel?: string;
  disableResponses?: boolean;
  disableAssign?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  showMoreMenu?: boolean;
  preferRawDescription?: boolean;
  variant?: 'default' | 'conditional';
  quickAction?: {
    ariaLabel: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
}

function getHeaderLabel(card: QuestionnaireCardData): string {
  const { isAssigned } = getQuestionnaireAssignmentMeta(card);
  if (card.status === 'draft') return 'Draft';
  return isAssigned ? 'Assigned' : 'Not Assigned';
}

function QuestionnaireMetricCard({
  label,
  value,
  icon,
  iconBg,
  iconColor,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '12px',
        bgcolor: '#fff',
        p: { xs: 1.5, sm: 2 },
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: iconBg,
            color: iconColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: { xs: '20px', sm: '22px' },
              lineHeight: 1,
              color: '#0f172a',
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '140%',
              letterSpacing: 0,
              color: '#667085',
              overflowWrap: 'anywhere',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export function QuestionnaireCard({
  card,
  onPreview,
  onResponses,
  onAssign,
  responsesLabel = 'Responses',
  disableResponses,
  disableAssign,
  onEdit,
  onDelete,
  showMoreMenu = true,
  preferRawDescription = false,
  variant = 'default',
  quickAction,
}: QuestionnaireCardProps) {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const { assignedCount, isAssigned } = getQuestionnaireAssignmentMeta(card);
  const responsesCount = card.responsesCount ?? 0;
  const headerLabel = getHeaderLabel(card);
  const showDeleteAction = card.type === 'custom' && variant !== 'conditional';
  const cardTitle = getCardTitle(card);
  const isConditionalModule = variant === 'conditional' || card.isConditionalModule === true;
  const showQuestionCountMetric = isConditionalModule || card.type !== 'custom' || isAssigned;
  const showEstimatedTimeMetric = isConditionalModule || card.type !== 'custom';
  const isDraft = card.status === 'draft';
  const totalQuestions = getCardQuestionCount(card);
  const cardDescription = getCardDescription(card).trim();
  const description = preferRawDescription
    ? cardDescription
    : card.type === 'custom'
      ? `${totalQuestions} ${totalQuestions === 1 ? 'question' : 'questions'}`
      : cardDescription;
  const responsesDisabled = disableResponses ?? isDraft;
  const assignDisabled = disableAssign ?? isDraft;

  return (
    <Card
      elevation={0}
      onClick={onPreview}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPreview();
        }
      }}
      sx={{
        overflow: 'hidden',
        borderRadius: '14px',
        border: '1px solid #dbe6eb',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          width: '100%',
          minHeight: 56,
          height: 56,
          opacity: 1,
          gap: 2,
          flexWrap: 'nowrap',
          px: { xs: 2, sm: 2.5 },
          py: 0,
          background: 'linear-gradient(90deg, #25a8da 0%, #34b6e2 50%, #58cfe9 100%)',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
            transform: { xs: 'translateY(-5px)', sm: 'translateY(-4px)' },
          }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#fff' }} />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: 1.2,
              color: '#fff',
            }}
          >
            {headerLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            justifySelf: 'end',
          }}
        >
          {quickAction && (
            <Tooltip
              title={card.type === 'core' ? QUICK_ACTION_TOOLTIP_MESSAGE : ''}
              placement="top"
              arrow
              disableHoverListener={card.type !== 'core'}
              disableFocusListener={card.type !== 'core'}
              disableTouchListener={card.type !== 'core'}
              slotProps={QUICK_ACTION_TOOLTIP_SLOT_PROPS}
            >
              <Box
                component="span"
                sx={{ display: 'inline-flex', order: 2 }}
              >
                <IconButton
                  data-action-tour-id="questionnaires-add-custom"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (quickAction.disabled || quickAction.loading) return;
                    quickAction.onClick();
                  }}
                  disabled={quickAction.disabled}
                  size="small"
                  aria-label={quickAction.ariaLabel}
                  sx={{
                    width: { xs: 44, sm: 36 },
                    height: { xs: 44, sm: 36 },
                    borderRadius: '14px',
                    bgcolor: 'transparent',
                    color: '#fff',
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                >
                  {quickAction.loading ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
                  ) : (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: { xs: 24, sm: 22 },
                        height: { xs: 30, sm: 26 },
                        position: 'relative',
                        display: 'inline-flex',
                        transform: { xs: 'translate(1px, 0)', sm: 'translate(1px, 0)' },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          left: { xs: 1, sm: 1 },
                          top: { xs: -4, sm: -3 },
                          width: { xs: 16, sm: 14 },
                          height: { xs: 20, sm: 18 },
                          borderRadius: '4px',
                          bgcolor: 'rgba(255,255,255,0.45)',
                        }}
                      />
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: { xs: 2, sm: 1 },
                          width: { xs: 18, sm: 16 },
                          height: { xs: 20, sm: 18 },
                          borderRadius: '4px',
                          bgcolor: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AddRoundedIcon sx={{ fontSize: { xs: 16, sm: 14 }, color: '#25A8DA' }} />
                      </Box>
                    </Box>
                  )}
                </IconButton>
              </Box>
            </Tooltip>
          )}

          {showMoreMenu && (
            <>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuAnchor(event.currentTarget);
                }}
                size="small"
                sx={{
                  width: { xs: 44, sm: 36 },
                  height: { xs: 44, sm: 36 },
                  borderRadius: '10px',
                  bgcolor: 'transparent',
                  color: '#fff',
                  transform: { xs: 'translateY(-4px)', sm: 'translateY(-3px)' },
                  '&:hover': { bgcolor: 'transparent' },
                }}
              >
                <MoreVertRoundedIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                onClick={(event) => event.stopPropagation()}
              >
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onPreview();
                  }}
                >
                  Preview questionnaire
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onAssign();
                  }}
                  disabled={assignDisabled}
                >
                  Assign to client
                </MenuItem>
                {showDeleteAction && onEdit && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onEdit();
                    }}
                  >
                    Edit questionnaire
                  </MenuItem>
                )}
                {showDeleteAction && onDelete && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onDelete();
                    }}
                    sx={{ color: '#b42318' }}
                  >
                    Delete questionnaire
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>

      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          pt: { xs: 2.5, sm: 3.5 },
          mt: { xs: -1.25, sm: -1.5 },
          borderTopLeftRadius: '14px',
          borderTopRightRadius: '14px',
          bgcolor: '#fff',
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2.5, sm: 3 }, flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'flex-start' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              <Tooltip title={cardTitle} placement="top" arrow>
                <Typography
                  component="span"
                  sx={{
                    ...cardHeadingSx,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {cardTitle}
                </Typography>
              </Tooltip>
              {description ? (
                <Typography
                  sx={{
                    mt: 1,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '15.05px',
                    lineHeight: { xs: '22px', sm: '24px' },
                    color: '#667085',
                    display: '-webkit-box',
                    WebkitLineClamp: { xs: 2, md: 3 },
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {description}
                </Typography>
              ) : null}
            </Box>

            <Tag
              variant="info"
              size="md"
              className="bg-[#E8F7FC] font-[Inter,sans-serif] text-[13px] font-semibold text-[#1599C8]"
              sx={{
                flexShrink: 0,
                alignSelf: { xs: 'stretch', md: 'flex-start' },
                height: { xs: 32, sm: 36 },
                maxWidth: { xs: '100%', sm: 'none' },
              }}
            >
              {(
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
                  <Box component="span">{`${responsesCount} Responses`}</Box>
                  <Box component="img" src={verifiedCheckIcon} alt="" sx={{ width: 16, height: 16, display: 'block' }} />
                </Box>
              )}
            </Tag>
          </Box>

          <Box
            sx={{
              borderRadius: '14px',
              bgcolor: '#f4f8fb',
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fit, minmax(135px, 1fr))',
                },
                gap: { xs: 1.5, sm: 2 },
                flex: 1,
                alignContent: 'flex-start',
              }}
            >
              {showQuestionCountMetric && (
                <QuestionnaireMetricCard
                  label="Total Questions"
                  value={getCardQuestionCount(card)}
                  icon={<Box component="img" src={notebookIcon} alt="" sx={{ width: 20, height: 20, display: 'block' }} />}
                  iconBg="#dff2fa"
                  iconColor="#1eaad8"
                />
              )}
              {showEstimatedTimeMetric && (
                <QuestionnaireMetricCard
                  label="Estimated Time"
                  value={`${getCardEstimatedMinutes(card)} min`}
                  icon={<Box component="img" src={clockCircleIcon} alt="" sx={{ width: 20, height: 20, display: 'block' }} />}
                  iconBg="#fff1da"
                  iconColor="#e5a93d"
                />
              )}
              <QuestionnaireMetricCard
                label="Assigned Clients"
                value={assignedCount}
                icon={
                  <Box
                    component="img"
                    src={usersGroupRoundedIcon}
                    alt=""
                    sx={{ width: 20, height: 20, display: 'block' }}
                  />
                }
                iconBg="#dff5fb"
                iconColor="#0891b2"
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
              gap: 1.5,
              width: '100%',
              mt: 'auto',
              flexWrap: { md: 'nowrap' },
            }}
          >
            <Button
              data-action-tour-id="questionnaires-preview"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onPreview();
              }}
              sx={{
                flex: 1,
                height: { xs: 44, sm: 38 },
                minHeight: { xs: 44, sm: 38 },
                borderRadius: '10px',
                border: '1px solid #d6dee6',
                color: '#344054',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                '&:hover': { borderColor: '#b9c7d3', bgcolor: '#f8fafc' },
              }}
            >
              Preview
            </Button>
          <Button
            data-action-tour-id="questionnaires-responses"
            variant="outlined"
            disabled={responsesDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onResponses();
              }}
              sx={{
                flex: 1,
                height: { xs: 44, sm: 38 },
                minHeight: { xs: 44, sm: 38 },
                borderRadius: '10px',
                border: '1px solid #d6dee6',
                color: '#344054',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                '&:hover': { borderColor: '#b9c7d3', bgcolor: '#f8fafc' },
              }}
            >
            {responsesLabel}
          </Button>
          <Button
            data-action-tour-id="questionnaires-assign"
            variant="contained"
            disabled={assignDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onAssign();
              }}
              sx={{
                flex: 1,
                height: { xs: 44, sm: 38 },
                minHeight: { xs: 44, sm: 38 },
                borderRadius: '10px',
                border: '1px solid transparent',
                bgcolor: '#25a8da',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                '&:hover': { bgcolor: '#1498ca' },
              }}
            >
              Assign
            </Button>
          </Box>
        </Box>
      </CardContent>
      <Box
        sx={{
          height: 4,
          bgcolor: '#25a8da',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
        }}
      />
    </Card>
  );
}
